import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button, Text, Spinner, Input, Textarea, Switch,
  makeStyles, mergeClasses, tokens, MessageBar, MessageBarBody,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions,
} from '@fluentui/react-components'
import {
  ArrowLeft24Regular, Save24Regular, Delete24Regular,
  ChevronUp20Regular, ChevronDown20Regular,
} from '@fluentui/react-icons'
import {
  documentDefinitionsService,
  DocTypeLabels,
  type SaveDocumentDefinitionRequest,
} from '../../services/documentDefinitionsService'
import { NativeDateInput } from '../../components/NativeDateInput'

const useStyles = makeStyles({
  pageHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '12px',
    boxShadow: tokens.shadow4,
    marginBottom: '16px',
  },
  cardHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 24px', cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground2 },
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  cardTitle: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
  fieldRow: {
    display: 'grid', gridTemplateColumns: '1fr 1fr',
    borderBottom: `1px solid ${tokens.colorNeutralBackground3}`,
  },
  fieldRowFull: { gridTemplateColumns: '1fr' },
  fieldCell: { padding: '10px 24px 12px', display: 'flex', flexDirection: 'column', gap: '6px' },
  fieldCellBorderRight: { borderRight: `1px solid ${tokens.colorNeutralBackground3}` },
  fieldLabel: {
    fontSize: '11px', fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase', letterSpacing: '0.5px',
  },
  hint: { fontSize: '11px', color: tokens.colorNeutralForeground3 },
  floatingBar: {
    position: 'sticky', bottom: '16px',
    display: 'flex', alignItems: 'center', gap: '10px',
    backgroundColor: '#1f1f1f', borderRadius: '12px',
    padding: '12px 20px',
    boxShadow: '0 8px 32px rgba(0,0,0,.25)',
    marginTop: '8px', zIndex: 10,
  },
  floatingHint: { fontSize: '13px', color: 'rgba(255,255,255,.6)', flex: '1' },
  radioRow: { display: 'flex', gap: '20px', marginTop: '4px' },
  radioLabel: { display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' },
})

const today = () => new Date().toISOString().slice(0, 10)

function emptyForm(): SaveDocumentDefinitionRequest {
  return {
    name: '',
    documentType: 2,
    documentUrl: '',
    note: '',
    pairingCode: '',
    validFrom: today(),
    validTo: null,
    assignOnHire: false,
    assignOnHireContractor: false,
    deadlineDate: null,
    deadlineDays: null,
    reminderDaysBefore: 7,
    isActive: true,
  }
}

function extractError(e: any): string {
  const d = e?.response?.data
  if (!d) return `Chyba při ukládání (${e?.message ?? 'síťová chyba'})`
  return d.detail ?? d.error ?? d.title
    ?? (d.errors ? Object.values(d.errors).flat().join(', ') : null)
    ?? `HTTP ${e?.response?.status ?? '?'}`
}

export default function DocumentDefinitionFormPage() {
  const styles = useStyles()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id
  const qc = useQueryClient()

  const [form, setForm] = useState<SaveDocumentDefinitionRequest>(emptyForm())
  const [openCard1, setOpenCard1] = useState(true)
  const [openCard2, setOpenCard2] = useState(true)
  const [serverError, setServerError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const savedFormRef = useRef<SaveDocumentDefinitionRequest>(emptyForm())

  const { data: detail, isLoading } = useQuery({
    queryKey: ['document-definition', id],
    queryFn: () => documentDefinitionsService.getById(Number(id)),
    enabled: isEdit,
  })

  useEffect(() => {
    if (detail) {
      const f: SaveDocumentDefinitionRequest = {
        name: detail.name,
        documentType: detail.documentType,
        documentUrl: detail.documentUrl ?? '',
        note: detail.note ?? '',
        pairingCode: detail.pairingCode ?? '',
        validFrom: detail.validFrom ? detail.validFrom.slice(0, 10) : null,
        validTo: detail.validTo ? detail.validTo.slice(0, 10) : null,
        assignOnHire: detail.assignOnHire,
        assignOnHireContractor: detail.assignOnHireContractor,
        deadlineDate: detail.deadlineDate ? detail.deadlineDate.slice(0, 10) : null,
        deadlineDays: detail.deadlineDays,
        reminderDaysBefore: detail.reminderDaysBefore ?? 7,
        isActive: detail.isActive,
      }
      setForm(f)
      savedFormRef.current = f
    }
  }, [detail])

  const isDirty = JSON.stringify(form) !== JSON.stringify(savedFormRef.current)

  const permanentDeleteMutation = useMutation({
    mutationFn: () => documentDefinitionsService.permanentDelete(Number(id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['document-definitions'] })
      navigate('/documents')
    },
    onError: (e: any) => setServerError(extractError(e)),
  })

  const saveMutation = useMutation<unknown, any, void>({
    mutationFn: () => {
      const req: SaveDocumentDefinitionRequest = {
        ...form,
        documentUrl: form.documentUrl || null,
        note: form.note || null,
        pairingCode: form.pairingCode || null,
        validFrom: form.validFrom || null,
        validTo: form.validTo || null,
        deadlineDate: form.deadlineDate || null,
        deadlineDays: form.deadlineDays ?? null,
        reminderDaysBefore: form.reminderDaysBefore ?? null,
      }
      return isEdit
        ? documentDefinitionsService.update(Number(id), req)
        : documentDefinitionsService.create(req)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['document-definitions'] })
      qc.invalidateQueries({ queryKey: ['document-definition', id] })
      savedFormRef.current = form
      setServerError(null)
      navigate('/documents')
    },
    onError: (e: any) => setServerError(extractError(e)),
  })

  if (isLoading) return <Spinner label="Načítám…" />

  return (
    <div>
      {/* Hlavička */}
      <div className={styles.pageHeader}>
        <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={() => navigate('/documents')} />
        <Text size={600} weight="semibold">
          {isEdit ? 'Upravit dokument' : 'Nový dokument'}
        </Text>
      </div>

      {serverError && (
        <MessageBar intent="error" style={{ marginBottom: 16 }}>
          <MessageBarBody>{serverError}</MessageBarBody>
        </MessageBar>
      )}

      {/* Karta 1 — Základní informace */}
      <div className={styles.card}>
        <div className={styles.cardHeader} onClick={() => setOpenCard1(o => !o)}>
          <span className={styles.cardTitle}>Definice dokumentu</span>
          {openCard1 ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
        </div>

        {openCard1 && (
          <>
            {/* Název */}
            <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
              <div className={styles.fieldCell}>
                <span className={styles.fieldLabel}>Název *</span>
                <Input
                  value={form.name}
                  onChange={(_, d) => setForm(f => ({ ...f, name: d.value }))}
                  placeholder="Název dokumentu"
                />
              </div>
            </div>

            {/* Typ dokumentu | Odkaz na dokument */}
            <div className={styles.fieldRow}>
              <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                <span className={styles.fieldLabel}>Typ dokumentu</span>
                <div className={styles.radioRow}>
                  {([2, 1, 3] as const).map(t => (
                    <label key={t} className={styles.radioLabel} style={isEdit ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}>
                      <input
                        type="radio"
                        name="docType"
                        checked={form.documentType === t}
                        disabled={isEdit}
                        onChange={() => setForm(f => ({
                          ...f,
                          documentType: t,
                          pairingCode: t !== 3 ? '' : f.pairingCode,
                        }))}
                      />
                      {DocTypeLabels[t]}
                    </label>
                  ))}
                </div>
                <span className={styles.hint}>
                  {isEdit
                    ? 'Typ dokumentu nelze po vytvoření změnit.'
                    : 'Podpis = HR potvrzuje, Seznámení = zaměstnanec potvrzuje sám, Školení LMS = výsledek z LMS importu'}
                </span>
              </div>
              <div className={styles.fieldCell}>
                <span className={styles.fieldLabel}>Odkaz na dokument (URL)</span>
                <Input
                  value={form.documentUrl ?? ''}
                  onChange={(_, d) => setForm(f => ({ ...f, documentUrl: d.value }))}
                  placeholder="https://..."
                />
              </div>
            </div>

            {/* Párovací kód — pouze pro typ Školení LMS */}
            {form.documentType === 3 && (
              <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
                <div className={styles.fieldCell}>
                  <span className={styles.fieldLabel}>Párovací kód pro import</span>
                  <Input
                    value={form.pairingCode ?? ''}
                    onChange={(_, d) => setForm(f => ({ ...f, pairingCode: d.value }))}
                    placeholder="Krátký název kurzu z LMS (např. AI Akt)"
                    style={{ maxWidth: '400px' }}
                  />
                  <span className={styles.hint}>Musí přesně odpovídat hodnotě ve sloupci „Krátký název kurzu s odkazem" v exportu z LMS SUDOP CIT</span>
                </div>
              </div>
            )}

            {/* Platnost od | Platnost do */}
            <div className={styles.fieldRow} style={{ borderBottom: 'none' }}>
              <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                <span className={styles.fieldLabel}>Platnost od</span>
                <NativeDateInput
                  value={form.validFrom ?? ''}
                  onChange={v => setForm(f => ({ ...f, validFrom: v }))}
                />
              </div>
              <div className={styles.fieldCell}>
                <span className={styles.fieldLabel}>Platnost do</span>
                <NativeDateInput
                  value={form.validTo ?? ''}
                  onChange={v => setForm(f => ({ ...f, validTo: v || null }))}
                  min={form.validFrom ?? undefined}
                />
                <span className={styles.hint}>Ponechte prázdné pro neomezenou platnost</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Karta 2 — Termín a přiřazení */}
      <div className={styles.card}>
        <div className={styles.cardHeader} onClick={() => setOpenCard2(o => !o)}>
          <span className={styles.cardTitle}>Termín splnění a přiřazení</span>
          {openCard2 ? <ChevronUp20Regular /> : <ChevronDown20Regular />}
        </div>

        {openCard2 && (
          <>
            {/* Pevný termín | Počet dní */}
            <div className={styles.fieldRow}>
              <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                <span className={styles.fieldLabel}>Pevný termín (datum)</span>
                <NativeDateInput
                  value={form.deadlineDate ?? ''}
                  onChange={v => setForm(f => ({
                    ...f,
                    deadlineDate: v || null,
                    deadlineDays: v ? null : f.deadlineDays,
                  }))}
                />
              </div>
              <div className={styles.fieldCell}>
                <span className={styles.fieldLabel}>Nebo: počet dní od zveřejnění / nástupu</span>
                <Input
                  type="number"
                  min="1"
                  value={form.deadlineDays != null ? String(form.deadlineDays) : ''}
                  onChange={(_, d) => setForm(f => ({
                    ...f,
                    deadlineDays: d.value ? Number(d.value) : null,
                    deadlineDate: d.value ? null : f.deadlineDate,
                  }))}
                  style={{ maxWidth: '160px' }}
                />
                <span className={styles.hint}>Termín se počítá od ValidFrom nebo nástupu, podle toho co nastalo později</span>
              </div>
            </div>

            {/* Upomínka */}
            <div className={mergeClasses(styles.fieldRow, styles.fieldRowFull)}>
              <div className={styles.fieldCell}>
                <span className={styles.fieldLabel}>Upomínka pro HR (dní před termínem)</span>
                <Input
                  type="number"
                  min="1"
                  value={form.reminderDaysBefore != null ? String(form.reminderDaysBefore) : ''}
                  onChange={(_, d) => setForm(f => ({ ...f, reminderDaysBefore: d.value ? Number(d.value) : null }))}
                  style={{ maxWidth: '160px' }}
                />
                <span className={styles.hint}>Zobrazí se v přehledu dokumentů s blížícím se termínem</span>
              </div>
            </div>

            {/* Přiřazení */}
            <div className={styles.fieldRow}>
              <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                <span className={styles.fieldLabel}>Automatické přiřazení nových zaměstnanců</span>
                <Switch
                  label="Automaticky přiřadit nově nastupujícím zaměstnancům"
                  checked={form.assignOnHire}
                  onChange={(_, d) => setForm(f => ({ ...f, assignOnHire: d.checked }))}
                />
              </div>
              <div className={styles.fieldCell}>
                <span className={styles.fieldLabel}>Automatické přiřazení nových externistů</span>
                <Switch
                  label="Automaticky přiřadit nově nastupujícím externistům"
                  checked={form.assignOnHireContractor}
                  onChange={(_, d) => setForm(f => ({ ...f, assignOnHireContractor: d.checked }))}
                />
              </div>
            </div>

            {/* Poznámka + Aktivní */}
            <div className={styles.fieldRow} style={{ borderBottom: 'none' }}>
              <div className={mergeClasses(styles.fieldCell, styles.fieldCellBorderRight)}>
                <span className={styles.fieldLabel}>Poznámka HR</span>
                <Textarea
                  rows={3}
                  value={form.note ?? ''}
                  onChange={(_, d) => setForm(f => ({ ...f, note: d.value }))}
                />
              </div>
              {isEdit && (
                <div className={styles.fieldCell}>
                  <span className={styles.fieldLabel}>Stav záznamu</span>
                  <Switch
                    label="Aktivní"
                    checked={form.isActive}
                    onChange={(_, d) => setForm(f => ({ ...f, isActive: d.checked }))}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Floating save bar */}
      <div className={styles.floatingBar}>
        {/* Trvale odstranit — jen když je uložený stav neaktivní */}
        {isEdit && detail?.isActive === false && (
          <Button
            appearance="subtle"
            icon={<Delete24Regular />}
            style={{ color: '#f87171' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.15)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            onClick={() => setDeleteOpen(true)}
          >
            Trvale odstranit
          </Button>
        )}
        <span className={styles.floatingHint}>{isDirty ? '✏ Neuložené změny' : ''}</span>
        <Button
          appearance="subtle"
          style={{ color: 'rgba(255,255,255,0.8)' }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
          onClick={() => navigate('/documents')}
        >
          Zrušit editaci
        </Button>
        <Button
          appearance="primary"
          icon={<Save24Regular />}
          onClick={() => {
            if (!form.name.trim()) { setServerError('Název je povinný.'); return }
            if (form.validTo && form.validFrom && form.validTo < form.validFrom) {
              setServerError('Datum ukončení nemůže být dříve než datum zahájení.')
              return
            }
            setServerError(null)
            saveMutation.mutate()
          }}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Ukládám…' : 'Uložit'}
        </Button>
      </div>

      {/* Dialog: trvalé smazání */}
      <Dialog open={deleteOpen} onOpenChange={(_, s) => setDeleteOpen(s.open)}>
        <DialogSurface>
          <DialogTitle>Trvale odstranit dokument?</DialogTitle>
          <DialogBody>
            <DialogContent>
              Opravdu chcete trvale odstranit dokument <strong>{form.name}</strong>?
              Tato akce je nevratná.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteOpen(false)}>Zrušit</Button>
              <Button
                appearance="primary"
                style={{ backgroundColor: tokens.colorPaletteRedBackground3, color: '#fff' }}
                icon={permanentDeleteMutation.isPending ? <Spinner size="tiny" /> : <Delete24Regular />}
                disabled={permanentDeleteMutation.isPending}
                onClick={() => permanentDeleteMutation.mutate()}
              >
                Trvale odstranit
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
